import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { FaSearch, FaChevronDown, FaTimes, FaSync } from "react-icons/fa";
import axios from "axios";

const StaffActivityFilters = ({
  selectedStaff,
  onStaffChange,
  searchTerm,
  onSearch,
  loading: tableLoading,
}) => {
  const [staffList, setStaffList] = useState([]);
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [ddSearch, setDdSearch] = useState("");
  const [ddLoading, setDdLoading] = useState(false);
  const [ddError, setDdError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);

  // Active users state
  const [rawActiveUserIds, setRawActiveUserIds] = useState([]);
  const [activeLoading, setActiveLoading] = useState(false);
  const [activeError, setActiveError] = useState(null);

  const dropdownRef = useRef(null);
  const ddSearchRef = useRef(null);
  const cancelTokenRef = useRef(null);
  const activeCancelTokenRef = useRef(null);
  const activeIntervalRef = useRef(null);

  // Compute active staff count
  const activeStaffCount = useMemo(() => {
    if (!staffList.length) return 0;
    const activeIdSet = new Set(rawActiveUserIds);
    return staffList.filter(staff => activeIdSet.has(staff._id)).length;
  }, [staffList, rawActiveUserIds]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && ddSearchRef.current) {
      setTimeout(() => ddSearchRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Cleanup staff axios cancel token
  useEffect(() => {
    return () => {
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel("Component unmounted");
      }
    };
  }, []);

  // Cleanup active users interval & cancel token
  useEffect(() => {
    return () => {
      if (activeIntervalRef.current) clearInterval(activeIntervalRef.current);
      if (activeCancelTokenRef.current) {
        activeCancelTokenRef.current.cancel("Component unmounted");
      }
    };
  }, []);

  // Fetch staff list
  const fetchStaff = useCallback(async () => {
    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel("New request initiated");
    }
    cancelTokenRef.current = axios.CancelToken.source();

    setDdLoading(true);
    setDdError(null);

    try {
      const { data: response } = await axios.get(
        `${process.env.REACT_APP_API_URL}/staff/get-all-staff`,
        {
          params: { page: 1, limit: 50 },
          cancelToken: cancelTokenRef.current.token,
        }
      );

      if (!response?.success) {
        throw new Error(response?.message || "Failed to fetch staff");
      }

      setStaffList(response.data);
      setFilteredStaff(response.data);
      setHasFetched(true);
    } catch (err) {
      if (axios.isCancel(err)) return;
      console.error("[StaffActivityFilters] fetchStaff:", err);
      setDdError(
        err?.response?.data?.message ||
          err.message ||
          "Failed to load staff. Please try again."
      );
    } finally {
      setDdLoading(false);
    }
  }, []);

  // Fetch connected users (active staff IDs)
  const fetchActiveUsers = useCallback(async () => {
    if (activeCancelTokenRef.current) {
      activeCancelTokenRef.current.cancel("New active users request");
    }
    activeCancelTokenRef.current = axios.CancelToken.source();

    setActiveLoading(true);
    setActiveError(null);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/connected-socket-users/connected-users`,
        {
          cancelToken: activeCancelTokenRef.current.token,
        }
      );

      const users = response.data?.users || [];
      const userIds = users.map(u => u.userId).filter(Boolean);
      setRawActiveUserIds(response.data?.users?.length);
    } catch (err) {
      if (axios.isCancel(err)) return;
      console.error("[StaffActivityFilters] fetchActiveUsers:", err);
      setActiveError("Failed to load active users");
    } finally {
      setActiveLoading(false);
    }
  }, []);

  // Auto-refresh active users every 30 seconds
  useEffect(() => {
    fetchActiveUsers();
    activeIntervalRef.current = setInterval(fetchActiveUsers, 30000);
    return () => {
      if (activeIntervalRef.current) clearInterval(activeIntervalRef.current);
    };
  }, [fetchActiveUsers]);

  // Client-side filter for dropdown
  const applyClientFilter = useCallback(
    (query) => {
      if (!query.trim()) {
        setFilteredStaff(staffList);
        return;
      }
      const q = query.toLowerCase();
      setFilteredStaff(
        staffList.filter(
          (s) =>
            s.fullName?.toLowerCase().includes(q) ||
            s.phoneNumber?.toLowerCase().includes(q) ||
            s.rolePermission?.rolename?.toLowerCase().includes(q)
        )
      );
    },
    [staffList]
  );

  const handleOpenDropdown = () => {
    setIsOpen((prev) => !prev);
    setDdSearch("");
    setFilteredStaff(staffList);
    if (!hasFetched) fetchStaff();
  };

  const handleDdSearchChange = (e) => {
    const val = e.target.value;
    setDdSearch(val);
    applyClientFilter(val);
  };

  const handleSelect = (staff) => {
    onStaffChange(staff);
    setIsOpen(false);
    setDdSearch("");
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onStaffChange(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") setIsOpen(false);
  };

  const getInitials = (name = "") =>
    name
      .split(" ")
      .map((p) => p[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

  const highlightMatch = (text = "", term = "") => {
    if (!term.trim()) return text;
    const idx = text.toLowerCase().indexOf(term.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark className="text-inherit rounded bg-pink-50 px-px">
          {text.slice(idx, idx + term.length)}
        </mark>
        {text.slice(idx + term.length)}
      </>
    );
  };

  const SkeletonRows = () => (
    <>
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-3 py-2.5">
          <div className="h-8 w-8 shrink-0 animate-pulse rounded-full bg-gray-200" />
          <div className="flex flex-1 flex-col gap-1.5">
            <div className="h-3 w-2/5 animate-pulse rounded bg-gray-200" />
            <div className="h-2.5 w-3/5 animate-pulse rounded bg-gray-100" />
          </div>
          <div className="h-5 w-14 shrink-0 animate-pulse rounded-full bg-gray-100" />
        </div>
      ))}
    </>
  );

  return (
    <div className="mb-6 rounded-lg bg-white p-4 shadow-sm sm:p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        {/* Global search */}
        <div className="w-full lg:w-96">
          <label className="mb-2 block text-sm font-medium text-gray-700">
            Search by staff, phone or action
          </label>
          <div className="relative">
            <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => onSearch(e.target.value)}
              placeholder="Search..."
              disabled={tableLoading}
              className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm
                         text-gray-900 placeholder-gray-400 transition-colors
                         focus:border-[#c51369] focus:outline-none focus:ring-1 focus:ring-[#c51369]
                         disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
        </div>

        {/* Staff dropdown with active count badge */}
        <div className="w-full lg:w-80" ref={dropdownRef}>
          <div className="mb-2 flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700">
              Select a staff
            </label>
            {/* Active count display */}
            <div className="flex items-center gap-2 text-xs">
              {activeLoading && <span className="text-gray-400">...</span>}
              {!activeLoading && activeError && (
                <span className="text-red-500" title={activeError}>
                  ⚠️
                </span>
              )}
              {!activeLoading && !activeError && (
                <div className="flex items-center gap-1.5 rounded-full bg-green-50 px-2 py-0.5">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500"></span>
                  </span>
                  <span className="font-medium text-green-700">
                    {/* {console.log('rawActiveUserIds', rawActiveUserIds)} */}
                    {rawActiveUserIds} active
                  </span>
                </div>
              )}
              <button
                onClick={() => fetchActiveUsers()}
                disabled={activeLoading}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                title="Refresh active count"
              >
                <FaSync size={10} className={activeLoading ? "animate-spin" : ""} />
              </button>
            </div>
          </div>

          <div className="relative">
            {/* Trigger button (unchanged) */}
            <button
              type="button"
              onClick={handleOpenDropdown}
              onKeyDown={handleKeyDown}
              disabled={tableLoading}
              aria-expanded={isOpen}
              aria-haspopup="listbox"
              className={`flex w-full items-center justify-between gap-2 rounded-lg border bg-white
                          px-4 py-2.5 text-left text-sm transition-colors
                          focus:outline-none focus:ring-1 focus:ring-[#c51369]
                          disabled:cursor-not-allowed disabled:opacity-50
                          ${
                            isOpen
                              ? "border-[#c51369] ring-1 ring-[#c51369]"
                              : "border-gray-300 hover:border-gray-400"
                          }`}
            >
              {selectedStaff ? (
                <span className="flex min-w-0 items-center gap-2">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-pink-100 text-[10px] font-semibold text-[#c51369]">
                    {getInitials(selectedStaff.fullName)}
                  </span>
                  <span className="truncate font-medium text-gray-900">
                    {selectedStaff.fullName}
                  </span>
                  <span className="shrink-0 text-xs text-gray-400">
                    {selectedStaff.rolePermission?.rolename}
                  </span>
                </span>
              ) : (
                <span className="text-gray-400">All staff</span>
              )}

              <span className="flex shrink-0 items-center gap-1">
                {selectedStaff && (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={handleClear}
                    onKeyDown={(e) => e.key === "Enter" && handleClear(e)}
                    className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    aria-label="Clear selection"
                  >
                    <FaTimes size={10} />
                  </span>
                )}
                <FaChevronDown
                  size={11}
                  className={`text-gray-400 transition-transform duration-200 ${
                    isOpen ? "rotate-180" : ""
                  }`}
                />
              </span>
            </button>

            {/* Dropdown panel (unchanged) */}
            {isOpen && (
              <div
                role="listbox"
                className="absolute left-0 right-0 top-[calc(100%+4px)] z-50 overflow-hidden
                           rounded-lg border border-gray-200 bg-white shadow-lg"
              >
                <div className="border-b border-gray-100 p-2">
                  <div className="relative">
                    <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400" />
                    <input
                      ref={ddSearchRef}
                      type="text"
                      value={ddSearch}
                      onChange={handleDdSearchChange}
                      onKeyDown={handleKeyDown}
                      placeholder="Search by name, phone or role..."
                      className="w-full rounded-md border border-gray-200 bg-gray-50 py-1.5
                                 pl-8 pr-3 text-sm text-gray-900 placeholder-gray-400
                                 focus:border-[#c51369] focus:outline-none focus:ring-1 focus:ring-[#c51369]"
                    />
                  </div>
                </div>

                <div className="max-h-56 overflow-y-auto">
                  {ddLoading && <SkeletonRows />}
                  {!ddLoading && ddError && (
                    <div className="flex flex-col items-center gap-2 px-4 py-5">
                      <p className="text-sm text-red-500">{ddError}</p>
                      <button
                        type="button"
                        onClick={fetchStaff}
                        className="text-xs font-medium text-[#c51369] underline hover:no-underline"
                      >
                        Retry
                      </button>
                    </div>
                  )}
                  {!ddLoading && !ddError && filteredStaff.length === 0 && (
                    <p className="px-4 py-5 text-center text-sm text-gray-400">
                      {ddSearch
                        ? "No staff match your search."
                        : "No staff found."}
                    </p>
                  )}
                  {!ddLoading && !ddError && !ddSearch && (
                    <div
                      role="option"
                      aria-selected={!selectedStaff}
                      onClick={() => handleSelect(null)}
                      className={`flex cursor-pointer items-center gap-3 px-3 py-2.5 text-sm
                                  hover:bg-gray-50 ${
                                    !selectedStaff ? "bg-pink-50" : ""
                                  }`}
                    >
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-medium text-gray-500">
                        All
                      </span>
                      <span className="font-medium text-gray-700">
                        All staff
                      </span>
                    </div>
                  )}
                  {!ddLoading &&
                    !ddError &&
                    filteredStaff.map((staff) => {
                      const isSelected = selectedStaff?.id === staff.id;
                      return (
                        <div
                          key={staff._id ?? staff.id}
                          role="option"
                          aria-selected={isSelected}
                          onClick={() => handleSelect(staff)}
                          className={`flex cursor-pointer items-center gap-3 px-3 py-2.5 text-sm
                                      hover:bg-gray-50 ${
                                        isSelected ? "bg-pink-50" : ""
                                      }`}
                        >
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-pink-100 text-xs font-semibold text-[#c51369]">
                            {getInitials(staff.fullName)}
                          </span>
                          <span className="flex min-w-0 flex-1 flex-col">
                            <span className="truncate font-medium text-gray-900">
                              {highlightMatch(staff.fullName, ddSearch)}
                            </span>
                            <span className="truncate text-xs text-gray-400">
                              {highlightMatch(staff.phoneNumber, ddSearch)}
                            </span>
                          </span>
                          {staff.rolePermission?.rolename && (
                            <span className="shrink-0 rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[10px] text-gray-500">
                              {staff.rolePermission.rolename}
                            </span>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffActivityFilters;






// import React, { useState, useEffect, useRef, useCallback } from "react";
// import { FaSearch, FaChevronDown, FaTimes } from "react-icons/fa";
// import axios from "axios";

// const StaffActivityFilters = ({
//   selectedStaff,
//   onStaffChange,
//   searchTerm,
//   onSearch,
//   loading: tableLoading,
// }) => {
//   const [staffList, setStaffList] = useState([]);
//   const [filteredStaff, setFilteredStaff] = useState([]);
//   const [isOpen, setIsOpen] = useState(false);
//   const [ddSearch, setDdSearch] = useState("");
//   const [ddLoading, setDdLoading] = useState(false);
//   const [ddError, setDdError] = useState(null);
//   const [hasFetched, setHasFetched] = useState(false);

//   const dropdownRef = useRef(null);
//   const ddSearchRef = useRef(null);
//   const cancelTokenRef = useRef(null); // axios cancel token

//   // Close dropdown on outside click
//   useEffect(() => {
//     const handler = (e) => {
//       if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
//         setIsOpen(false);
//       }
//     };
//     document.addEventListener("mousedown", handler);
//     return () => document.removeEventListener("mousedown", handler);
//   }, []);

//   // Focus search input when dropdown opens
//   useEffect(() => {
//     if (isOpen && ddSearchRef.current) {
//       setTimeout(() => ddSearchRef.current?.focus(), 50);
//     }
//   }, [isOpen]);

//   // Cleanup axios cancel token on unmount
//   useEffect(() => {
//     return () => {
//       if (cancelTokenRef.current) {
//         cancelTokenRef.current.cancel("Component unmounted");
//       }
//     };
//   }, []);

//   const fetchStaff = useCallback(async () => {
//     // Cancel any in-flight request
//     if (cancelTokenRef.current) {
//       cancelTokenRef.current.cancel("New request initiated");
//     }
//     cancelTokenRef.current = axios.CancelToken.source();

//     setDdLoading(true);
//     setDdError(null);

//     try {
//       // Replace the fetchStaff axios call URL:

//       const { data: response } = await axios.get(
//         `${process.env.REACT_APP_API_URL}/staff/get-all-staff`, // ← staff dropdown endpoint
//         {
//           params: { page: 1, limit: 50 },
//           cancelToken: cancelTokenRef.current.token,
//         }
//       );

//       if (!response?.success) {
//         throw new Error(response?.message || "Failed to fetch staff");
//       }

//       setStaffList(response.data);
//       setFilteredStaff(response.data);
//       setHasFetched(true);
//     } catch (err) {
//       if (axios.isCancel(err)) return; // silently ignore cancellations
//       console.error("[StaffActivityFilters] fetchStaff:", err);
//       setDdError(
//         err?.response?.data?.message ||
//           err.message ||
//           "Failed to load staff. Please try again."
//       );
//     } finally {
//       setDdLoading(false);
//     }
//   }, []);

//   // Client-side filter for instant response while typing
//   const applyClientFilter = useCallback(
//     (query) => {
//       if (!query.trim()) {
//         setFilteredStaff(staffList);
//         return;
//       }
//       const q = query.toLowerCase();
//       setFilteredStaff(
//         staffList.filter(
//           (s) =>
//             s.fullName?.toLowerCase().includes(q) ||
//             s.phoneNumber?.toLowerCase().includes(q) ||
//             s.rolePermission?.rolename?.toLowerCase().includes(q)
//         )
//       );
//     },
//     [staffList]
//   );

//   const handleOpenDropdown = () => {
//     setIsOpen((prev) => !prev);
//     setDdSearch("");
//     setFilteredStaff(staffList);
//     if (!hasFetched) fetchStaff();
//   };

//   const handleDdSearchChange = (e) => {
//     const val = e.target.value;
//     setDdSearch(val);
//     applyClientFilter(val);
//   };

//   const handleSelect = (staff) => {
//     onStaffChange(staff);
//     setIsOpen(false);
//     setDdSearch("");
//   };

//   const handleClear = (e) => {
//     e.stopPropagation();
//     onStaffChange(null);
//   };

//   const handleKeyDown = (e) => {
//     if (e.key === "Escape") setIsOpen(false);
//   };

//   const getInitials = (name = "") =>
//     name
//       .split(" ")
//       .map((p) => p[0])
//       .join("")
//       .slice(0, 2)
//       .toUpperCase();

//   const highlightMatch = (text = "", term = "") => {
//     if (!term.trim()) return text;
//     const idx = text.toLowerCase().indexOf(term.toLowerCase());
//     if (idx === -1) return text;
//     return (
//       <>
//         {text.slice(0, idx)}
//         <mark className="text-inherit rounded bg-pink-50 px-px">
//           {text.slice(idx, idx + term.length)}
//         </mark>
//         {text.slice(idx + term.length)}
//       </>
//     );
//   };

//   // ── Skeleton loader rows ──────────────────────────────────────────────────
//   const SkeletonRows = () => (
//     <>
//       {Array.from({ length: 4 }).map((_, i) => (
//         <div key={i} className="flex items-center gap-3 px-3 py-2.5">
//           <div className="h-8 w-8 shrink-0 animate-pulse rounded-full bg-gray-200" />
//           <div className="flex flex-1 flex-col gap-1.5">
//             <div className="h-3 w-2/5 animate-pulse rounded bg-gray-200" />
//             <div className="h-2.5 w-3/5 animate-pulse rounded bg-gray-100" />
//           </div>
//           <div className="h-5 w-14 shrink-0 animate-pulse rounded-full bg-gray-100" />
//         </div>
//       ))}
//     </>
//   );

//   return (
//     <div className="mb-6 rounded-lg bg-white p-4 shadow-sm sm:p-6">
//       <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
//         {/* ── Global search ─────────────────────────────────────────────── */}
//         <div className="w-full lg:w-96">
//           <label className="mb-2 block text-sm font-medium text-gray-700">
//             Search by staff, phone or action
//           </label>
//           <div className="relative">
//             <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
//             <input
//               type="text"
//               value={searchTerm}
//               onChange={(e) => onSearch(e.target.value)}
//               placeholder="Search..."
//               disabled={tableLoading}
//               className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm
//                          text-gray-900 placeholder-gray-400 transition-colors
//                          focus:border-[#c51369] focus:outline-none focus:ring-1 focus:ring-[#c51369]
//                          disabled:cursor-not-allowed disabled:opacity-50"
//             />
//           </div>
//         </div>

//         {/* ── Staff dropdown ─────────────────────────────────────────────── */}
//         <div className="w-full lg:w-80" ref={dropdownRef}>
//           <label className="mb-2 block text-sm font-medium text-gray-700">
//             Select a staff
//           </label>
//           <div className="relative">
//             {/* Trigger button */}
//             <button
//               type="button"
//               onClick={handleOpenDropdown}
//               onKeyDown={handleKeyDown}
//               disabled={tableLoading}
//               aria-expanded={isOpen}
//               aria-haspopup="listbox"
//               className={`flex w-full items-center justify-between gap-2 rounded-lg border bg-white
//                           px-4 py-2.5 text-left text-sm transition-colors
//                           focus:outline-none focus:ring-1 focus:ring-[#c51369]
//                           disabled:cursor-not-allowed disabled:opacity-50
//                           ${
//                             isOpen
//                               ? "border-[#c51369] ring-1 ring-[#c51369]"
//                               : "border-gray-300 hover:border-gray-400"
//                           }`}
//             >
//               {/* Selected value or placeholder */}
//               {selectedStaff ? (
//                 <span className="flex min-w-0 items-center gap-2">
//                   <span
//                     className="flex h-6 w-6 shrink-0 items-center justify-center
//                                    rounded-full bg-pink-100 text-[10px] font-semibold text-[#c51369]"
//                   >
//                     {getInitials(selectedStaff.fullName)}
//                   </span>
//                   <span className="truncate font-medium text-gray-900">
//                     {selectedStaff.fullName}
//                   </span>
//                   <span className="shrink-0 text-xs text-gray-400">
//                     {selectedStaff.rolePermission?.rolename}
//                   </span>
//                 </span>
//               ) : (
//                 <span className="text-gray-400">All staff</span>
//               )}

//               <span className="flex shrink-0 items-center gap-1">
//                 {/* Clear button */}
//                 {selectedStaff && (
//                   <span
//                     role="button"
//                     tabIndex={0}
//                     onClick={handleClear}
//                     onKeyDown={(e) => e.key === "Enter" && handleClear(e)}
//                     className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
//                     aria-label="Clear selection"
//                   >
//                     <FaTimes size={10} />
//                   </span>
//                 )}
//                 <FaChevronDown
//                   size={11}
//                   className={`text-gray-400 transition-transform duration-200 ${
//                     isOpen ? "rotate-180" : ""
//                   }`}
//                 />
//               </span>
//             </button>

//             {/* Dropdown panel */}
//             {isOpen && (
//               <div
//                 role="listbox"
//                 className="absolute left-0 right-0 top-[calc(100%+4px)] z-50 overflow-hidden
//                            rounded-lg border border-gray-200 bg-white shadow-lg"
//               >
//                 {/* Inline search */}
//                 <div className="border-b border-gray-100 p-2">
//                   <div className="relative">
//                     <FaSearch
//                       className="pointer-events-none absolute left-3 top-1/2
//                                          -translate-y-1/2 text-xs text-gray-400"
//                     />
//                     <input
//                       ref={ddSearchRef}
//                       type="text"
//                       value={ddSearch}
//                       onChange={handleDdSearchChange}
//                       onKeyDown={handleKeyDown}
//                       placeholder="Search by name, phone or role..."
//                       className="w-full rounded-md border border-gray-200 bg-gray-50 py-1.5
//                                  pl-8 pr-3 text-sm text-gray-900 placeholder-gray-400
//                                  focus:border-[#c51369] focus:outline-none focus:ring-1 focus:ring-[#c51369]"
//                     />
//                   </div>
//                 </div>

//                 {/* List body */}
//                 <div className="max-h-56 overflow-y-auto">
//                   {/* Loading skeleton */}
//                   {ddLoading && <SkeletonRows />}

//                   {/* Error state */}
//                   {!ddLoading && ddError && (
//                     <div className="flex flex-col items-center gap-2 px-4 py-5">
//                       <p className="text-sm text-red-500">{ddError}</p>
//                       <button
//                         type="button"
//                         onClick={fetchStaff}
//                         className="text-xs font-medium text-[#c51369] underline hover:no-underline"
//                       >
//                         Retry
//                       </button>
//                     </div>
//                   )}

//                   {/* Empty state */}
//                   {!ddLoading && !ddError && filteredStaff.length === 0 && (
//                     <p className="px-4 py-5 text-center text-sm text-gray-400">
//                       {ddSearch
//                         ? "No staff match your search."
//                         : "No staff found."}
//                     </p>
//                   )}

//                   {/* "All staff" option */}
//                   {!ddLoading && !ddError && !ddSearch && (
//                     <div
//                       role="option"
//                       aria-selected={!selectedStaff}
//                       onClick={() => handleSelect(null)}
//                       className={`flex cursor-pointer items-center gap-3 px-3 py-2.5 text-sm
//                                   hover:bg-gray-50 ${
//                                     !selectedStaff ? "bg-pink-50" : ""
//                                   }`}
//                     >
//                       <span
//                         className="flex h-8 w-8 shrink-0 items-center justify-center
//                                        rounded-full bg-gray-100 text-xs font-medium text-gray-500"
//                       >
//                         All
//                       </span>
//                       <span className="font-medium text-gray-700">
//                         All staff
//                       </span>
//                     </div>
//                   )}

//                   {/* Staff list */}
//                   {!ddLoading &&
//                     !ddError &&
//                     filteredStaff.map((staff) => {
//                       const isSelected = selectedStaff?.id === staff.id;
//                       return (
//                         <div
//                           key={staff._id ?? staff.id}
//                           role="option"
//                           aria-selected={isSelected}
//                           onClick={() => handleSelect(staff)}
//                           className={`flex cursor-pointer items-center gap-3 px-3 py-2.5 text-sm
//                                       hover:bg-gray-50 ${
//                                         isSelected ? "bg-pink-50" : ""
//                                       }`}
//                         >
//                           {/* Avatar */}
//                           <span
//                             className="flex h-8 w-8 shrink-0 items-center justify-center
//                                            rounded-full bg-pink-100 text-xs font-semibold text-[#c51369]"
//                           >
//                             {getInitials(staff.fullName)}
//                           </span>

//                           {/* Info */}
//                           <span className="flex min-w-0 flex-1 flex-col">
//                             <span className="truncate font-medium text-gray-900">
//                               {highlightMatch(staff.fullName, ddSearch)}
//                             </span>
//                             <span className="truncate text-xs text-gray-400">
//                               {highlightMatch(staff.phoneNumber, ddSearch)}
//                             </span>
//                           </span>

//                           {/* Role badge */}
//                           {staff.rolePermission?.rolename && (
//                             <span
//                               className="shrink-0 rounded-full border border-gray-200
//                                              bg-gray-50 px-2 py-0.5 text-[10px] text-gray-500"
//                             >
//                               {staff.rolePermission.rolename}
//                             </span>
//                           )}
//                         </div>
//                       );
//                     })}
//                 </div>
//               </div>
//             )}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default StaffActivityFilters;
